import * as XLSX from 'xlsx';

const HIDE_EMAIL_NOT_SENT_STEPS = new Set(['unfulfilled', 'preorder_received']);

export function formatTrackingDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatStepForExport(step, inPipeline, stepCode) {
  if (!inPipeline) return 'Not valid';
  if (!step || !step.reached) return 'Not reached';

  const parts = ['Reached'];
  if (step.changed_at) {
    parts.push(formatTrackingDateTime(step.changed_at));
  }

  const hideEmail = HIDE_EMAIL_NOT_SENT_STEPS.has(stepCode);
  if (step.email_sent) {
    parts.push('Email sent');
    if (step.email_sent_at) {
      parts.push(formatTrackingDateTime(step.email_sent_at));
    }
  } else if (!hideEmail) {
    parts.push('Status skip');
  }

  if (stepCode === 'review' && step.review_submitted) {
    parts.push('Review submitted');
  }

  return parts.join(' | ');
}

export function buildTrackingExportRows(orders, stepColumns) {
  return orders.map((order) => {
    const stepMap = Object.fromEntries((order.steps || []).map((s) => [s.code, s]));
    const pipelineCodes = new Set((order.steps || []).map((s) => s.code));

    const row = {
      'Order Number': order.order_number || '',
      'Order Date': formatTrackingDateTime(order.ordered_at),
      'Customer Email': order.customer_email || '',
      'Customer Name': order.customer_name || '',
      'Current Status': order.current_status_label || '',
      'Order Type': order.order_type_label || '',
      'Shipping Type': order.is_digital ? 'Digital' : 'Shipping',
      'Payment Status': order.payment_status_label || order.payment_status || '',
      'Books': order.books || (order.product_titles || []).join(', '),
      'Total': order.total_price ? `${order.total_price} ${order.currency || ''}`.trim() : '',
      'AWB / Tracking': order.tracking_number || '',
      'Review Submitted': order.review_submitted ? 'Yes' : 'No',
    };

    for (const col of stepColumns) {
      row[col.label] = formatStepForExport(
        stepMap[col.code],
        pipelineCodes.has(col.code),
        col.code,
      );
    }

    return row;
  });
}

function downloadBlob(blob, filename) {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function exportTrackingToCSV(rows) {
  if (!rows.length) return false;

  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((key) => `"${String(row[key] ?? '').replace(/"/g, '""')}"`)
        .join(','),
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv; charset=utf-8;' });
  const stamp = new Date().toISOString().split('T')[0];
  downloadBlob(blob, `order-tracking-export-${stamp}.csv`);
  return true;
}

export function exportTrackingToExcel(rows) {
  if (!rows.length) return false;

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Order Tracking');
  const stamp = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `order-tracking-export-${stamp}.xlsx`);
  return true;
}

/**
 * Prefer server-side streaming export (no 50k-row client RAM).
 * Falls back only if the API method is missing.
 */
export async function downloadTrackingExportFromServer(databaseAPI, filters, format) {
  const res = await databaseAPI.downloadTrackingExport({
    ...filters,
    format,
  });
  if (!res.success) {
    throw new Error(res.error || 'Export failed');
  }
  downloadBlob(res.blob, res.filename);
  return true;
}

/** @deprecated Prefer downloadTrackingExportFromServer — kept for small in-memory cases */
export async function fetchAllTrackingOrders(databaseAPI, filters, { maxRows = 3000 } = {}) {
  const all = [];
  let offset = null;
  let page = 0;
  const maxPages = Math.ceil(maxRows / 100);

  while (page < maxPages) {
    const res = await databaseAPI.getOrdersTracking({
      ...filters,
      limit: 100,
      offset,
    });
    if (!res.success) {
      throw new Error(res.error || 'Failed to fetch tracking data for export');
    }
    all.push(...(res.orders || []));
    if (all.length >= maxRows) {
      return { orders: all.slice(0, maxRows), truncated: true };
    }
    if (!res.next_offset) break;
    offset = res.next_offset;
    page += 1;
  }

  return { orders: all, truncated: false };
}
